import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Play, Clock, CheckCircle, Lock, ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingEnroll, setProcessingEnroll] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const courseRes = await axios.get(`${API_URL}/api/courses/${courseId}`);
        setCourse(courseRes.data);
        
        if (courseRes.data.modules?.length > 0) {
          setActiveModule(courseRes.data.modules[0]);
        }

        if (user) {
          try {
            const enrollmentRes = await axios.get(`${API_URL}/api/enrollments/${courseId}`, {
              headers: getAuthHeader()
            });
            setEnrollment(enrollmentRes.data);
          } catch (e) {
            // Not enrolled
          }
        }
      } catch (error) {
        console.error("Error fetching course:", error);
        toast.error("Course not found");
        navigate("/school");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, user, getAuthHeader, navigate]);

  const handleEnroll = async () => {
    if (!user) {
      toast.info("Please sign in to enroll");
      navigate("/login", { state: { from: `/school/course/${courseId}` } });
      return;
    }

    setProcessingEnroll(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          course_id: courseId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process enrollment. Please try again.");
    } finally {
      setProcessingEnroll(false);
    }
  };

  const handleModuleComplete = async (moduleId) => {
    if (!enrollment) return;

    try {
      await axios.post(
        `${API_URL}/api/enrollments/${courseId}/progress?module_id=${moduleId}&completed=true`,
        {},
        { headers: getAuthHeader() }
      );
      
      setEnrollment(prev => ({
        ...prev,
        progress: { ...prev.progress, [moduleId]: true }
      }));
      
      toast.success("Module completed!");
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const getProgress = () => {
    if (!enrollment || !course?.modules) return 0;
    const completed = Object.values(enrollment.progress || {}).filter(Boolean).length;
    return Math.round((completed / course.modules.length) * 100);
  };

  const isModuleCompleted = (moduleId) => {
    return enrollment?.progress?.[moduleId] === true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div data-testid="course-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <button 
            onClick={() => navigate("/school")}
            className="flex items-center gap-2 text-gray-300 hover:text-white mb-4"
          >
            <ChevronLeft className="h-5 w-5" />
            Back to Courses
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <span className="text-[#9D9045] text-sm font-medium">{course.category}</span>
              <h1 className="text-2xl md:text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                {course.title}
              </h1>
            </div>
            {enrollment && (
              <div className="flex items-center gap-4">
                <div className="text-white text-sm">
                  Progress: {getProgress()}%
                </div>
                <div className="w-32">
                  <Progress value={getProgress()} className="h-2 bg-white/20" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-video bg-[#1E3A5F] relative flex items-center justify-center video-placeholder">
                {enrollment ? (
                  <div className="text-center text-white z-10">
                    <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 hover:bg-white/30 transition-colors cursor-pointer">
                      <Play className="h-10 w-10 text-white ml-1" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{activeModule?.title}</h3>
                    <p className="text-gray-300 text-sm">Click to play video</p>
                    <p className="text-gray-400 text-xs mt-2">(Video content coming soon)</p>
                  </div>
                ) : (
                  <div className="text-center text-white z-10">
                    <Lock className="h-16 w-16 mx-auto mb-4 text-white/50" />
                    <h3 className="text-xl font-semibold mb-2">Enroll to Access</h3>
                    <p className="text-gray-300">This content is available to enrolled students</p>
                  </div>
                )}
              </div>
              
              {enrollment && activeModule && (
                <div className="p-6 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-[#1E3A5F]">{activeModule.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{activeModule.description}</p>
                    </div>
                    {!isModuleCompleted(activeModule.id) && (
                      <Button 
                        onClick={() => handleModuleComplete(activeModule.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="mark-complete-btn"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Mark Complete
                      </Button>
                    )}
                    {isModuleCompleted(activeModule.id) && (
                      <span className="flex items-center gap-2 text-green-600 font-medium">
                        <CheckCircle className="h-5 w-5" />
                        Completed
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Course Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4">About This Course</h3>
              <p className="text-gray-600">{course.description}</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Enrollment Card */}
            {!enrollment && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-3xl font-bold text-[#1E3A5F] mb-2">
                  ${course.price?.toFixed(0)}
                </div>
                <p className="text-sm text-gray-500 mb-6">One-time payment • Lifetime access</p>
                <Button 
                  onClick={handleEnroll}
                  disabled={processingEnroll}
                  className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6"
                  data-testid="enroll-now-btn"
                >
                  {processingEnroll ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Enroll Now"
                  )}
                </Button>
              </div>
            )}

            {/* Module List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b">
                <h3 className="font-semibold text-[#1E3A5F]">Course Modules</h3>
                <p className="text-sm text-gray-500">{course.modules?.length || 0} lessons</p>
              </div>
              <div className="divide-y">
                {course.modules?.map((module, index) => {
                  const isCompleted = isModuleCompleted(module.id);
                  const isActive = activeModule?.id === module.id;
                  const isLocked = !enrollment;
                  
                  return (
                    <button
                      key={module.id}
                      onClick={() => enrollment && setActiveModule(module)}
                      disabled={isLocked}
                      className={`w-full p-4 text-left flex items-start gap-3 transition-colors ${
                        isActive ? "bg-[#1E3A5F]/5" : "hover:bg-gray-50"
                      } ${isLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`module-${module.id}`}
                    >
                      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? "bg-green-100 text-green-600" 
                          : isActive 
                            ? "bg-[#1E3A5F] text-white" 
                            : "bg-gray-100 text-gray-500"
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : isLocked ? (
                          <Lock className="h-4 w-4" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${isActive ? "text-[#1E3A5F]" : "text-gray-700"}`}>
                          {module.title}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <Clock className="h-3 w-3" />
                          <span>{module.duration_minutes} min</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
